import type { Components, Theme } from "@mui/material/styles";
import { RadiusToken } from "../../tokens/radii";

// Mirrors MuiOutlinedInput. The picker's accessible-DOM-structure input
// (v8 default — `PickersTextField` → `PickersOutlinedInput`) renders a
// `sectionsContainer` of date segments instead of a single `input` slot.
// The notched outline lives at `.MuiPickersOutlinedInput-notchedOutline`,
// but the sections container is inherited from `PickersInputBase` and
// keeps that prefix (`.MuiPickersInputBase-sectionsContainer`) — targeting
// the OutlinedInput prefix here silently fails.
//
// Padding matches MuiOutlinedInput's `size="medium"` variant. The picker's
// slot type doesn't accept `variants[]` and its ownerState doesn't expose
// `size`, so `small` is not differentiated here — DatePicker is currently
// only used at the default (medium) size in the app.
export const MuiPickersOutlinedInput: NonNullable<
  Components<Theme>["MuiPickersOutlinedInput"]
> = {
  styleOverrides: {
    root: ({ theme }) => {
      const rmh = theme.vars!.palette.rmh;
      return {
        borderRadius: RadiusToken.MD,
        "& .MuiPickersInputBase-sectionsContainer": {
          ...theme.typography.bodyMdRegular,
          padding: `${theme.spacing(2.5)} 0`,
        },
        "& .MuiPickersOutlinedInput-notchedOutline": {
          borderRadius: RadiusToken.MD,
          transition: theme.transitions.create(
            ["border-color", "border-width"],
            { duration: theme.transitions.duration.shortest }
          ),
          borderColor: rmh.border["1-surface"].rest,
          borderWidth: "1px",
        },
        transition: theme.transitions.create(
          ["background-color", "box-shadow"],
          { duration: theme.transitions.duration.shortest }
        ),
        backgroundColor: rmh.paper["1-surface"].rest,

        "&:hover:not(.Mui-disabled, .Mui-focused, .Mui-error) .MuiPickersOutlinedInput-notchedOutline":
          {
            borderColor: rmh.border["4-faint"].hover,
          },
        "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
          borderColor: rmh.border["5-brand"].rest,
          borderWidth: "1.5px",
        },
        "&.Mui-error .MuiPickersOutlinedInput-notchedOutline": {
          borderColor: rmh.border["8-error"].rest,
          borderWidth: "1px",
        },
        "&.Mui-focused.Mui-error .MuiPickersOutlinedInput-notchedOutline": {
          borderColor: rmh.border["8-error"].rest,
          borderWidth: "1.5px",
        },
        "&.Mui-disabled .MuiPickersOutlinedInput-notchedOutline": {
          borderColor: rmh.stateBorder.disabled,
          borderWidth: "1px",
        },
        "&.Mui-disabled": {
          backgroundColor: rmh.stateBackground.disabledInput,
        },
        "&.Mui-focused:not(.Mui-error)": {
          boxShadow: rmh.focus.primary,
        },
        "&.Mui-focused.Mui-error": {
          boxShadow: rmh.focus.error,
        },
      };
    },
  },
};
